import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

import Icon from '@mdi/react';

import { ROUNDED_CONTROL_RADIUS } from 'components/theme/controlRadius';

/**
 * Outline and fills shared by every segmented control (and the plain icon
 * buttons next to them) in the bank: the outline is the one the inputs and
 * outlined buttons use (Button's `input` colour), the fills are translucent
 * so the control stays low-key on any surface.
 * @param {Object} theme MUI theme
 * @returns {{outline: string, hoverFill: string, selectedFill: string}}
 */
export const getSegmentedControlColors = (theme) => {
  const isLight = theme.name === 'light';
  return {
    outline: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
    hoverFill: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
    selectedFill: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.16)',
  };
};

const useStyles = makeStyles(
  (theme) => {
    const { outline, hoverFill, selectedFill } =
      getSegmentedControlColors(theme);
    return {
      group: {
        flexShrink: 0,
        borderRadius: 4,
      },
      groupFullWidth: {
        display: 'flex',
        width: '100%',
      },
      // Softer outer corners (opt-in): only the first segment's left and the
      // last segment's right corners, so the seams between segments stay
      // square; three classes outrank MUI's two-class grouped rules.
      groupRounded: {
        borderRadius: ROUNDED_CONTROL_RADIUS,
      },
      segmentRounded: {},
      // The size of the house Button (40px); the active segment gets a
      // translucent fill and full-strength content, the others stay muted.
      segment: {
        height: theme.spacing(5),
        minWidth: theme.spacing(5),
        padding: theme.spacing(0, 1.5),
        color: theme.palette.text.tertiary,
        border: `1px solid ${outline}`,
        borderRadius: 4,
        textTransform: 'none',
        fontFamily: theme.typography.fontPanton.fontFamily,
        fontSize: '0.8rem',
        lineHeight: 1,
        '&:hover': {
          backgroundColor: hoverFill,
        },
        '&$selected': {
          color: theme.palette.text.primary,
          backgroundColor: selectedFill,
          '&:hover': {
            backgroundColor: selectedFill,
          },
        },
        '&$disabled': {
          color: theme.palette.text.disabled,
          borderColor: outline,
        },
        '&$segmentRounded:first-child': {
          borderTopLeftRadius: ROUNDED_CONTROL_RADIUS,
          borderBottomLeftRadius: ROUNDED_CONTROL_RADIUS,
        },
        '&$segmentRounded:last-child': {
          borderTopRightRadius: ROUNDED_CONTROL_RADIUS,
          borderBottomRightRadius: ROUNDED_CONTROL_RADIUS,
        },
      },
      disabled: {},
      segmentIconOnly: {
        width: theme.spacing(5),
        padding: 0,
      },
      segmentFullWidth: {
        flex: 1,
        minWidth: 0,
      },
      selected: {},
      label: {
        marginLeft: theme.spacing(0.75),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    };
  },
  { name: 'LixBankSegmentedControl' }
);

// A disabled segment with a reason takes the pointer again (MUI's disabled
// button has `pointer-events: none`), so its tooltip shows on hover.
const REASON_STYLE = { pointerEvents: 'auto' };

/**
 * The bank's segmented control: an exclusive ToggleButtonGroup whose
 * segments show an @mdi/js icon and, with `showLabels`, the label (dropped
 * again below the md breakpoint, where only the icons fit); the label is
 * always the accessible name and the tooltip. Clicking the active segment
 * is a no-op; `onChange` receives the next key. Extra props go to the group,
 * extra option fields (e.g. `data-*`, `disabled`) to their segment. A
 * disabled segment's `disabledReason` follows the label in its tooltip
 * ("Kartta – Ladataan"): the segment takes the pointer for that, where MUI
 * leaves a disabled button without one, and it still takes no click.
 * @param {Object} props
 * @param {Array<{key: string, label: string, iconPath?: string, icon?: React.ReactNode, disabled?: boolean, disabledReason?: string}>} props.options
 * @param {string} props.value active option key
 * @param {Function} [props.onChange] called with the next key
 * @param {boolean} [props.showLabels=false] render the labels next to the icons
 * @param {boolean} [props.fullWidth=false] stretch the segments over the container
 * @param {boolean} [props.rounded=false] softer outer corners (ROUNDED_CONTROL_RADIUS)
 * @param {string} [props.ariaLabel] accessible name of the group
 * @param {string} [props.className]
 */
export function BankSegmentedControl({
  options,
  value,
  onChange,
  showLabels,
  fullWidth,
  rounded,
  ariaLabel,
  className,
  ...groupProps
}) {
  const classes = useStyles();
  const theme = useTheme();
  // `noSsr` so the first render already knows the width: a phone must not
  // draw the labels for a frame before dropping them.
  const compact = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const labelsVisible = Boolean(showLabels) && !compact;

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      aria-label={ariaLabel}
      className={clsx(
        classes.group,
        fullWidth && classes.groupFullWidth,
        rounded && classes.groupRounded,
        className
      )}
      data-bank-segmented="true"
      onChange={(event, next) => {
        if (next && onChange) onChange(next);
      }}
      {...groupProps}
    >
      {options.map(
        ({ key, label, iconPath, icon, disabledReason, ...segmentProps }) => {
          const reason = segmentProps.disabled && disabledReason;
          return (
            <ToggleButton
              key={key}
              value={key}
              classes={{
                root: clsx(
                  classes.segment,
                  !labelsVisible && classes.segmentIconOnly,
                  fullWidth && classes.segmentFullWidth,
                  rounded && classes.segmentRounded
                ),
                selected: classes.selected,
                disabled: classes.disabled,
              }}
              aria-label={label}
              title={reason ? `${label} – ${reason}` : label}
              style={reason ? REASON_STYLE : undefined}
              data-disabled-reason={reason || undefined}
              {...segmentProps}
            >
              {iconPath ? <Icon path={iconPath} size={0.9} /> : icon}
              {labelsVisible ? (
                <span className={classes.label}>{label}</span>
              ) : null}
            </ToggleButton>
          );
        }
      )}
    </ToggleButtonGroup>
  );
}

BankSegmentedControl.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      iconPath: PropTypes.string,
      icon: PropTypes.node,
      disabled: PropTypes.bool,
      /** Why a disabled segment is off, for its tooltip. */
      disabledReason: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  showLabels: PropTypes.bool,
  fullWidth: PropTypes.bool,
  rounded: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

BankSegmentedControl.defaultProps = {
  showLabels: false,
  fullWidth: false,
};

export default BankSegmentedControl;
